import * as Yup from 'yup';
import moment from 'moment';
import * as isLuhn from 'isluhn';

const validateCardNumber = (number) => {
  // eslint-disable-next-line no-undef
  return isLuhn(number) && OpenPay.card.validateCardNumber(number);
};

const validateDateFormat = (date) => {
  return moment(date, 'DD/MM/YYYY', true).isValid();
};

const validDateRange = (date) => {
  const dateNow = moment(new Date());
  const dateGiven = moment(date, 'DD/MM/YYYY', true);
  const dateDiff = dateNow.diff(dateGiven, 'years');
  const rangeValid = dateDiff <= 70 && dateDiff >= 25;

  return rangeValid;
};

const validateRFC = (rfc) => {
  const rfcTrimmed = rfc ? rfc.trim() : rfc;
  return /[A-ZÑ&]{3,4}\d{6}[A-V1-9][A-Z1-9][0-9A]/.test(rfcTrimmed);
};

const validateCURP = (curp) => {
  const curpTrimmed = curp ? curp.trim() : curp;
  return /^([A-Z][AEIOUX][A-Z]{2}\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])[HM](?:AS|B[CS]|C[CLMSH]|D[FG]|G[TR]|HG|JC|M[CNS]|N[ETL]|OC|PL|Q[TR]|S[PLR]|T[CSL]|VZ|YN|ZS)[B-DF-HJ-NP-TV-Z]{3}[A-Z\d])(\d)$/
    .test(curpTrimmed);
};

const validatePostalCode = (code) => {
  return /^[0-9]{5}$/.test(code ? code.trim() : code);
};

export const FORM1_SCHEMA = {
  Nombre: Yup.string().required('Campo Requerido').matches(/^[a-z A-Zá-úÁ-Ú\s]+$/, 'Sin números ni caracteres especiales'),
  Paterno: Yup.string().required('Campo Requerido').matches(/^[a-z A-Zá-úÁ-Ú\s]+$/, 'Sin números ni caracteres especiales'),
  Materno: Yup.string().required('Campo Requerido').matches(/^[a-z A-Zá-úÁ-Ú\s]+$/, 'Sin números ni caracteres especiales'),
  Telefono: Yup.string().required('Campo Requerido').matches(/^[0-9]{10}$/, 'Solo diez números'),
  Email: Yup.string().required('Campo requerido').email('Email inválido'),
  FechaNacimiento: Yup.string().required('Campo requerido')
    .test(
      'validDOB',
      'Fecha inválida',
      validateDateFormat,
    )
    .test(
      'validDateRange',
      'Fecha fuera de rango',
      validDateRange,
    ),
  isAceptTCGD: Yup.boolean().oneOf([true], 'Campo requerido'),
  AvisoPrivacidad: Yup.boolean().oneOf([true], 'Campo requerido'),
};

export const FORM2_SCHEMA = {
  question1: Yup.string().required('Campo Requerido').oneOf(['1', '2'], 'Seleccione una opción váldia'),
  question2: Yup.string().required('Campo Requerido').oneOf(['3', '4', '5', '6', '7'], 'Seleccione una opción váldia'),
  question4: Yup.string().required('Campo Requerido').oneOf(['16', '17', '18', '19'], 'Seleccione una opción váldia'),
};

export const FORM4_SCHEMA = {
  name: Yup.string().required('Campo Requerido').matches(/^[a-z A-Zá-úÁ-Ú\s]+$/, 'Sin números ni caracteres especiales'),
  email: Yup.string().required('Campo requerido').email('Email inválido'),
  cardNumber: Yup.number()
    .required('Campo Requerido')
    .test(
      'validCC',
      'Número no válido',
      validateCardNumber,
    ),
  cardMonth: Yup.number().required('Campo Requerido'),
  cardYear: Yup.number().required('Campo Requerido'),
  cardCode: Yup.number()
    .required('Campo Requerido')
    .moreThan(99, 'Número no válido')
    .lessThan(10000, 'Número no válido'),
};

export const FORM5_SCHEMA = {
  selectedSex: Yup.string().required('Campo Requerido').oneOf(['m', 'f'], 'Seleccione una opción váldia'),
  nationality: Yup.string(),
  birthCountry: Yup.string(),
  birthState: Yup.string().required('Campo Requerido'),
  email: Yup.string().required('Campo requerido').email('Email inválido'),
  rfc: Yup.string().required('Campo requerido').test(
    'validRFC',
    'RFC inválido',
    validateRFC,
  ),
  curp: Yup.string().required('Campo requerido').test(
    'validCURP',
    'CURP inválido',
    validateCURP,
  ),
  street: Yup.string().required('Campo Requerido').matches(/^[0-9a-z A-Zá-úÁ-Ú,.\s]+$/, 'Sin números ni caracteres especiales'),
  noExt: Yup.string().required('Campo Requerido'),
  noInt: Yup.string(),
  postalCode: Yup.string().required('Campo Requerido')
    .test(
      'validPC',
      'Código postal no válido',
      validatePostalCode,
    ),
  colony: Yup.string().required('Campo Requerido'),
  municipality: Yup.string().required('Campo Requerido'),
  state: Yup.string().required('Campo Requerido'),
  samePerson: Yup.string().required('Campo Requerido').oneOf(['yes', 'no'], 'Seleccione una opción váldia'),
  wantInvoice: Yup.string().required('Campo Requerido').oneOf(['yes', 'no'], 'Seleccione una opción váldia'),
  taxRegime: Yup.string().when(
    'wantInvoice',
    {
      is: (wantInvoice) => wantInvoice === 'yes',
      then: Yup.string().required('Campo Requerido'),
    },
  ),
  insuredName: Yup.string().when(
    'samePerson',
    {
      is: (samePerson) => samePerson === 'no',
      then: Yup.string()
        .required('Campo Requerido')
        .matches(/^[a-z A-Zá-úÁ-Ú\s]+$/, 'Sin números ni caracteres especiales'),
    },
  ),
  insuredFatherLastName: Yup.string().when(
    'samePerson',
    {
      is: (samePerson) => samePerson === 'no',
      then: Yup.string()
        .required('Campo Requerido')
        .matches(/^[a-z A-Zá-úÁ-Ú\s]+$/, 'Sin números ni caracteres especiales'),
    },
  ),
  insuredMotherLastName: Yup.string().when(
    'samePerson',
    {
      is: (samePerson) => samePerson === 'no',
      then: Yup.string()
        .required('Campo Requerido')
        .matches(/^[a-z A-Zá-úÁ-Ú\s]+$/, 'Sin números ni caracteres especiales'),
    },
  ),
  insuredBirthDate: Yup.string().when(
    'samePerson',
    {
      is: (samePerson) => samePerson === 'no',
      then: Yup.string().required('Campo requerido')
        .test(
          'validDOB',
          'Fecha inválida',
          validateDateFormat,
        )
        .test(
          'validDateRange',
          'Fecha fuera de rango',
          validDateRange,
        ),
    },
  ),
  insuredPhone: Yup.string().when(
    'samePerson',
    {
      is: (samePerson) => samePerson === 'no',
      then: Yup.string().required('Campo Requerido').matches(/^[0-9]{10}$/, 'Solo diez números'),
    },
  ),
  insuredEmail: Yup.string().when(
    'samePerson',
    {
      is: (samePerson) => samePerson === 'no',
      then: Yup.string().required('Campo requerido').email('Email inválido'),
    },
  ),
  insuredMaritalStatus: Yup.string().when(
    'samePerson',
    {
      is: (samePerson) => samePerson === 'no',
      then: Yup.string().required('Campo Requerido'),
    },
  ),
  insuredSelectedSex: Yup.string().when(
    'samePerson',
    {
      is: (samePerson) => samePerson === 'no',
      then: Yup.string()
        .required('Campo Requerido')
        .oneOf(['m', 'f'], 'Seleccione una opción váldia'),
    },
  ),
  insuredNationality: Yup.string(),
  insuredBirthCountry: Yup.string(),
  insuredBirthState: Yup.string().when(
    'samePerson',
    {
      is: (samePerson) => samePerson === 'no',
      then: Yup.string().required('Campo Requerido'),
    },
  ),
  insuredCurp: Yup.string().when(
    'samePerson',
    {
      is: (samePerson) => samePerson === 'no',
      then: Yup.string().required('Campo requerido').test(
        'validCURP',
        'CURP inválido',
        validateCURP,
      ),
    },
  ),
  insuredRfc: Yup.string().when(
    'samePerson',
    {
      is: (samePerson) => samePerson === 'no',
      then: Yup.string().required('Campo requerido').test(
        'validRFC',
        'RFC inválido',
        validateRFC,
      ),
    },
  ),
  insuredStreet: Yup.string().when(
    'samePerson',
    {
      is: (samePerson) => samePerson === 'no',
      then: Yup.string()
        .required('Campo Requerido')
        .matches(/^[0-9a-z A-Zá-úÁ-Ú\s]+$/, 'Sin números ni caracteres especiales'),
    },
  ),
  insuredNoExt: Yup.string().when(
    'samePerson',
    {
      is: (samePerson) => samePerson === 'no',
      then: Yup.string().required('Campo Requerido'),
    },
  ),
  insuredNoInt: Yup.string(),
  insuredPostalCode: Yup.string().when(
    'samePerson',
    {
      is: (samePerson) => samePerson === 'no',
      then: Yup.string().required('Campo Requerido')
        .test(
          'validPC',
          'Código postal no válido',
          validatePostalCode,
        ),
    },
  ),
  insuredColony: Yup.string().when(
    'samePerson',
    {
      is: (samePerson) => samePerson === 'no',
      then: Yup.string().required('Campo Requerido'),
    },
  ),
  insuredMunicipality: Yup.string().when(
    'samePerson',
    {
      is: (samePerson) => samePerson === 'no',
      then: Yup.string().required('Campo Requerido'),
    },
  ),
  insuredState: Yup.string().when(
    'samePerson',
    {
      is: (samePerson) => samePerson === 'no',
      then: Yup.string().required('Campo Requerido'),
    },
  ),
  insuredPrivacyStatements: Yup.boolean().when(
    'samePerson',
    {
      is: (samePerson) => samePerson === 'no',
      then: Yup.boolean().oneOf([true], 'Campo requerido'),
    },
  ),
};

export const FORM6_SCHEMA = {
  taxRegime: Yup.string().required('Campo Requerido'),
  email: Yup.string().required('Campo requerido').email('Email inválido'),
  rfc: Yup.string().required('Campo requerido').test(
    'validRFC',
    'RFC inválido',
    validateRFC,
  ),
  postalCode: Yup.string().required('Campo Requerido')
    .test(
      'validPC',
      'Código postal no válido',
      validatePostalCode,
    ),
};
